import React, { useState, useEffect } from 'react';
import API from '../utils/api';
import './VerificationStatus.css';

const VerificationStatus = ({ bookingId, listingType }) => {
  const [handoffStatus, setHandoffStatus] = useState(null);
  const [shipmentStatus, setShipmentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [bookingId]);

  const fetchStatus = async () => {
    try {
      if (listingType === 'rent') {
        const res = await API.get(`/bookings/${bookingId}/handoff-status`);
        setHandoffStatus(res.data);
      } else {
        const res = await API.get(`/bookings/${bookingId}/shipment-status`);
        setShipmentStatus(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch verification status:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="verification-status loading">⏳ Loading verification status...</div>;
  }

  if (listingType === 'rent') {
    return (
      <div className="verification-status">
        <h4>📸 Handoff Verification</h4>
        {handoffStatus?.status === 'not_started' && (
          <div className="status-box pending">
            <div className="status-icon">⏳</div>
            <div className="status-content">
              <div className="status-title">Awaiting Verification</div>
              <div className="status-desc">Both parties need to submit handoff photos</div>
            </div>
          </div>
        )}

        {handoffStatus?.status === 'verified' && (
          <div className="status-box verified">
            <div className="status-icon">✓</div>
            <div className="status-content">
              <div className="status-title">Handoff Verified</div>
              <div className="status-desc">Both parties confirmed the handoff with matching location & time</div>
            </div>
          </div>
        )}

        {handoffStatus?.status === 'location_mismatch' && (
          <div className="status-box error">
            <div className="status-icon">⚠️</div>
            <div className="status-content">
              <div className="status-title">Location Mismatch Detected</div>
              <div className="status-desc">Photos show different locations (more than 300 feet apart)</div>
            </div>
          </div>
        )}

        {handoffStatus?.status === 'timestamp_mismatch' && (
          <div className="status-box error">
            <div className="status-icon">⚠️</div>
            <div className="status-content">
              <div className="status-title">Timestamp Mismatch Detected</div>
              <div className="status-desc">Photos taken more than 10 minutes apart</div>
            </div>
          </div>
        )}

        <div className="verification-details">
          {handoffStatus?.renter_confirmed && (
            <div className="detail-item verified">
              <span className="icon">✓</span>
              <span>Renter confirmed</span>
            </div>
          )}
          {handoffStatus?.seller_confirmed && (
            <div className="detail-item verified">
              <span className="icon">✓</span>
              <span>Seller confirmed</span>
            </div>
          )}
          {!handoffStatus?.renter_confirmed && (
            <div className="detail-item pending">
              <span className="icon">⏳</span>
              <span>Awaiting renter confirmation</span>
            </div>
          )}
          {!handoffStatus?.seller_confirmed && (
            <div className="detail-item pending">
              <span className="icon">⏳</span>
              <span>Awaiting seller confirmation</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="verification-status">
      <h4>📦 Shipment Verification</h4>
      {shipmentStatus?.status === 'not_started' && (
        <div className="status-box pending">
          <div className="status-icon">⏳</div>
          <div className="status-content">
            <div className="status-title">Awaiting Shipment</div>
            <div className="status-desc">Seller needs to submit outbound shipment photos and tracking</div>
          </div>
        </div>
      )}

      {shipmentStatus?.status === 'outbound_in_transit' && (
        <div className="status-box pending">
          <div className="status-icon">📤</div>
          <div className="status-content">
            <div className="status-title">Item in Transit</div>
            <div className="status-desc">
              {shipmentStatus?.outbound_tracking?.number && (
                <>Tracking: {shipmentStatus.outbound_tracking.number}</>
              )}
            </div>
          </div>
        </div>
      )}

      {shipmentStatus?.status === 'return_in_transit' && (
        <div className="status-box pending">
          <div className="status-icon">📥</div>
          <div className="status-content">
            <div className="status-title">Return in Transit</div>
            <div className="status-desc">
              {shipmentStatus?.return_tracking?.number && (
                <>Tracking: {shipmentStatus.return_tracking.number}</>
              )}
            </div>
          </div>
        </div>
      )}

      {shipmentStatus?.status === 'verified' && (
        <div className="status-box verified">
          <div className="status-icon">✓</div>
          <div className="status-content">
            <div className="status-title">Return Verified</div>
            <div className="status-desc">Seller confirmed receipt of return. Deposit released.</div>
          </div>
        </div>
      )}

      <div className="verification-timeline">
        <div className={`timeline-step ${shipmentStatus?.outbound_confirmed ? 'done' : ''}`}>
          <div className="timeline-marker">1</div>
          <div className="timeline-label">Outbound Shipped</div>
        </div>
        <div className={`timeline-step ${shipmentStatus?.return_confirmed ? 'done' : ''}`}>
          <div className="timeline-marker">2</div>
          <div className="timeline-label">Return Shipped</div>
        </div>
        <div className={`timeline-step ${shipmentStatus?.return_received ? 'done' : ''}`}>
          <div className="timeline-marker">3</div>
          <div className="timeline-label">Return Received</div>
        </div>
      </div>
    </div>
  );
};

export default VerificationStatus;
